'use client';

// Third Party
import * as React from 'react';
import Image from 'next/image';
import { GalleryAsset } from '@gql';

// Utils
import { imagekitLoader } from '@utils/imagekit-loader';
import { GRAY_PIXEL_DATA_URL, zIndex } from '@utils/constants';

// UI
import { BaseModal } from '@components/base-modal';
import { IconNames } from '@components/icon';
import { ButtonIcon } from '@components/button-icon';

type Props = {
  modalIsOpen: boolean;
  closeModal: VoidFunction;
  originalIndex: number;
  galleryImages: Pick<GalleryAsset, 'description' | 'url'>[];
  altTag: string;
};

export const ImageGalleryModal: React.FC<Props> = ({
  modalIsOpen,
  galleryImages,
  closeModal,
  originalIndex,
  altTag,
}) => {
  const galleryImagesCount = galleryImages.length - 1;
  const [galleryIndex, setGalleryIndex] = React.useState(originalIndex);

  React.useEffect(() => {
    if (!modalIsOpen) return;
    const clampedIndex = Math.min(
      Math.max(originalIndex, 0),
      Math.max(galleryImages.length - 1, 0)
    );
    setGalleryIndex(clampedIndex);
  }, [modalIsOpen, originalIndex, galleryImages.length]);

  const handleCloseModal = (): void => {
    closeModal();
  };

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        setGalleryIndex((prevIndex) => Math.max(prevIndex - 1, 0));
      } else if (event.key === 'ArrowRight') {
        setGalleryIndex((prevIndex) =>
          Math.min(prevIndex + 1, galleryImages.length - 1)
        );
      }
    },
    [galleryImages.length]
  );

  // Arrow key navigation for gallery (escape is handled by BaseModal)
  React.useEffect(() => {
    if (!modalIsOpen) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalIsOpen, handleKeyDown]);

  return (
    <BaseModal
      isOpen={modalIsOpen}
      onClose={handleCloseModal}
      size="full"
      showCloseButton={false}
      ariaLabel="Image gallery"
    >
      <div className="fixed inset-0 bg-white transform transition-all w-full h-full">
        <div className="absolute inset-0 px-4 sm:px-0 w-full h-full lg:hidden">
          <div
            className="fixed flex flex-row justify-start top-2 left-2 w-full"
            style={{ zIndex: zIndex.tooltip }}
          >
            <ButtonIcon
              ariaLabel="Close gallery"
              icon={IconNames.X}
              onClick={handleCloseModal}
              variant="outlined"
              size="sm"
              color="gray"
              className="inline-flex items-center p-4 h-4 w-4 justify-center bg-white"
              isCircle={true}
            />
          </div>
          <div
            className="h-full pb-16 pt-10 space-y-6 overflow-y-auto scroll-smooth"
            ref={(el) => {
              if (el) {
                const imageElement = el.children[galleryIndex];
                if (imageElement) {
                  imageElement.scrollIntoView({ behavior: 'instant' });
                }
              }
            }}
          >
            {galleryImages.map((galleryImage, index) => (
              <div key={index} className="w-full aspect-3 relative">
                <Image
                  fill
                  src={galleryImage.url || ''}
                  className="object-center object-cover"
                  alt={altTag}
                  placeholder="blur"
                  loader={imagekitLoader}
                  blurDataURL={GRAY_PIXEL_DATA_URL}
                  sizes="calc(100vw - (2 * 20px))"
                  loading="eager"
                />
              </div>
            ))}
          </div>
        </div>
        <div
          className="hidden lg:block absolute inset-0"
          style={{ height: '95%' }}
        >
          <div className="relative flex flex-row justify-center items-center p-4">
            <ButtonIcon
              ariaLabel="Close gallery"
              icon={IconNames.X}
              onClick={handleCloseModal}
              variant="outlined"
              size="md"
              color="gray"
              className="inline-flex items-center p-4 h-10 w-10 justify-center"
              isCircle={true}
            />
            <span className="mx-auto text-gray-main">{`${galleryIndex + 1} / ${
              galleryImagesCount + 1
            }`}</span>
          </div>

          <div className="flex flex-row mx-auto w-full h-[calc(100%-4rem)] px-4">
            <div className="flex flex-col justify-center h-full">
              <ButtonIcon
                ariaLabel="Previous image"
                icon={IconNames.CHEVRON_LEFT}
                onClick={() => {
                  if (galleryIndex === 0) {
                    setGalleryIndex(galleryImagesCount);
                  } else {
                    setGalleryIndex(galleryIndex - 1);
                  }
                }}
                size="lg"
                color="gray"
                variant="text"
              />
            </div>

            <div className="mx-auto w-full h-full lg:w-3/4 relative">
              <div className="relative w-full h-full">
                <Image
                  fill
                  src={galleryImages[galleryIndex]?.url || ''}
                  className="object-center object-contain"
                  alt={altTag}
                  placeholder="blur"
                  loader={imagekitLoader}
                  blurDataURL={GRAY_PIXEL_DATA_URL}
                  sizes="75vw"
                  loading="eager"
                />
              </div>
            </div>

            <div className="flex flex-col justify-center h-full">
              <ButtonIcon
                ariaLabel="Next image"
                icon={IconNames.CHEVRON_RIGHT}
                onClick={() => {
                  if (galleryIndex === galleryImagesCount) {
                    setGalleryIndex(0);
                  } else {
                    setGalleryIndex(galleryIndex + 1);
                  }
                }}
                size="lg"
                color="gray"
                variant="text"
              />
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};
